export type PdfFormFieldType = 'text' | 'checkbox' | 'signature' | 'initial' | 'date' | 'email' | 'phone' | 'number';
export type PdfFormNumericFormat = 'number' | 'currency';

export interface PdfFormTemplate {
  id: number;
  company_id: number;
  name: string;
  s3_path: string;
  pdf_url?: string;
  has_signature: boolean;
  status: string;
  fields?: PdfFormField[];
  created_at: string;
  updated_at: string;
}

export interface PdfFormField {
  id?: number;
  pdf_form_template_id?: number;
  field_name: string;
  field_type: PdfFormFieldType;
  numeric_format?: PdfFormNumericFormat;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  font_size?: number;
  user_fillable?: boolean;
  required?: boolean;
}

export interface EditorField extends PdfFormField {
  _clientId: string;
}

export interface PdfFormSubmission {
  id: number;
  uuid: string;
  template: PdfFormTemplate;
  company: any;
  project: any;
  creator: any;
  client_name: string;
  client_email: string;
  client_phone: string | null;
  status: 'pending' | 'signed' | 'void';
  prefilled_url: string | null;
  signed_url: string | null;
  short_url: string | null;
  field_values?: Record<string, string | boolean | number> | null;
  resolved_token_values?: Record<string, string | boolean | number> | null;
  signed_at: string | null;
  emailed_at: string | null;
  created_at: string;
  updated_at: string;
}

export const FIELD_TYPES = ['text', 'checkbox', 'date', 'signature', 'initial', 'email', 'phone', 'number'] as const;

export interface TokenItem {
  label: string;
  value: string;
  type: string;
}

export interface TokenGroup {
  label: string;
  tokens: TokenItem[];
}

// Static fallback used when the /pdf-form-tokens API is unavailable.
export const FALLBACK_TOKEN_GROUPS: TokenGroup[] = [
  {
    label: 'Company',
    tokens: [
      { label: 'Company Name', value: '{{company}}', type: 'text' },
      { label: 'Company Address', value: '{{company_address}}', type: 'text' },
    ],
  },
  {
    label: 'Project',
    tokens: [
      { label: 'Job Number', value: '{{job_no}}', type: 'text' },
      { label: 'Project Alias', value: '{{project_alias}}', type: 'text' },
      { label: 'Project Address (Street)', value: '{{project}}', type: 'text' },
      { label: 'Project Address (Full)', value: '{{project_address}}', type: 'text' },
    ],
  },
  {
    label: 'Customer & Claims',
    tokens: [
      { label: 'Policy Holder Name', value: '{{policy_holder_name}}', type: 'text' },
      { label: 'Policy Number', value: '{{policy_number}}', type: 'text' },
      { label: 'Customer Phone', value: '{{customer_phone}}', type: 'phone' },
      { label: 'Customer Email', value: '{{customer_email}}', type: 'email' },
      { label: 'Claim Number', value: '{{claim_number}}', type: 'text' },
      { label: 'Insurance Provider', value: '{{provider}}', type: 'text' },
      { label: 'Representative', value: '{{representative}}', type: 'text' },
      { label: 'Ownership Status', value: '{{ownership_status}}', type: 'text' },
      { label: 'Insurance Deductible', value: '{{insurance_deductible}}', type: 'number' },
      { label: 'Adjuster Name', value: '{{adjuster}}', type: 'text' },
      { label: 'Adjuster Phone', value: '{{adjuster_phone}}', type: 'phone' },
      { label: 'Adjuster Email', value: '{{adjuster_email}}', type: 'email' },
    ],
  },
  {
    label: 'Damage',
    tokens: [
      { label: 'Types of Damage', value: '{{types_of_damage}}', type: 'text' },
      { label: 'Cause of Damage', value: '{{cause_of_damage}}', type: 'text' },
    ],
  },
  {
    label: 'Other',
    tokens: [
      { label: 'Current Date', value: '{{current_date}}', type: 'date' },
      { label: 'Date of Loss', value: '{{date_of_loss}}', type: 'date' },
      { label: 'User Name', value: '{{name}}', type: 'text' },
    ],
  },
];

// Module-level cache populated by fetchPdfFormTokens, seeded with fallback
let tokenLabelMap = new Map<string, string>(
  FALLBACK_TOKEN_GROUPS.flatMap((g) => g.tokens).map((t) => [t.value, t.label])
);

export function setTokenData(groups: TokenGroup[]) {
  const all = groups.flatMap((g) => g.tokens);
  tokenLabelMap = new Map(all.map((t) => [t.value, t.label]));
}

export function getTokenDisplayLabel(fieldName: string): string {
  if (fieldName === 'user_date') return 'User Date';
  return tokenLabelMap.get(fieldName) || fieldName;
}
